import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import {
  AlertTriangle, ArrowLeft, Download, Eye, Timer, TrendingDown, TrendingUp, UserMinus, Users,
} from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { c, t, sp, r, btn, card, tbl, chart } from '../theme';

// Which pages of this portal get used, by how many people, for how long.
//
// super_admin only, on the same argument as the Access Log: the page detail
// names who opened a page and how often, which is a record of what named
// colleagues did. Reads are access-logged by the mount in routes/index.js, so
// looking at this leaves its own trace.
//
// It reads the raw tables from migration 096 with no rollup in between — see
// the header of services/usageReport.js for why — so every number on screen is
// the events themselves rather than a second copy that could disagree.

const TZ = 'America/Los_Angeles';

// The portal's own pages. Routes are stored lowercased with ids collapsed to
// `:id` (services/usageAnalytics.js routeOf), so these keys are in that shape.
// A route with no entry falls back to its own path, which is never wrong —
// only less readable — so a new page missing from here degrades quietly.
const ROUTE_LABELS = {
  '/': 'Home',
  '/login': 'Sign in',
  '/review': 'My Reviews',
  '/admin': 'Periods',
  '/admin/company': 'MOR Dashboard',
  '/admin/users': 'User Management',
  '/admin/access-log': 'Access Log',
  '/admin/usage': 'Usage',
  '/admin/upload': 'Upload',
  '/admin/job-profiles': 'Job Profiles',
  '/admin/inventory-locations': 'Inventory Locations',
  '/admin/fuel-cards': 'Fuel Cards',
  '/admin/expense-mappings': 'Expense Mappings',
  '/admin/sales-mappings': 'Sales Mappings',
  '/admin/hr-headcount-upload': 'HR Headcount Upload',
  '/admin/staffing-tracker-upload': 'Staffing Tracker Upload',
  '/admin/staffing-job-requisitions-upload': 'Upload Job Requisitions',
  '/admin/staffing-job-req-categories': 'Job Req Categories',
  '/admin/overtime-budget': 'Overtime Budget',
  '/overtime': 'Overtime',
  '/overtime/definitions': 'Overtime Definitions',
  '/overtime/upload': 'Overtime Upload',
  '/inventory': 'Inventory',
  '/inventory/upload': 'Inventory Upload',
  '/fuel-report': 'Fuel Report',
  '/time-wage-audit': 'Time & Wage Audit',
  '/time-wage-audit/upload': 'Time & Wage Audit Upload',
  '/expense-analysis': 'Expense Analysis',
  '/expense-analysis/upload': 'Expense Upload',
  '/customer-billing': 'Customer Billing',
  '/customer-billing/upload': 'Customer Billing Upload',
  '/post-collections': 'Daily Productivity Report',
  '/softpak-reports': 'Soft-Pak Reports',
  '/fleet-reports': 'Fleet Reports',
  '/fleet-dashboard': 'R&M Dashboard',
  '/post-collections/upload': 'Post Collections Upload',
  '/post-collections/rules': 'Post Collections Rules',
  '/pricing': 'City Pricing',
  '/staffing': 'Staffing',
  '/staffing-tracker': 'Staffing Tracker',
  '/hr-headcount': 'HR Headcount',
  '/sales-report': 'Sales Report',
  '/sales-report/upload': 'Sales Upload',
  '/contact-center': 'Contact Center',
  '/contact-center/exec': 'Contact Center — Executive',
  '/contact-center/vp': 'Contact Center — VP',
  '/contact-center/director': 'Contact Center — Director',
  '/contact-center/manager': 'Contact Center — Manager',
  '/contact-center/supervisor': 'Contact Center — Supervisor',
  '/contact-center/leadership': 'Contact Center — Leadership',
  '/contact-center/team': 'Contact Center — Team',
  '/contact-center/agents': 'Contact Center — Agents',
  '/contact-center/live': 'Contact Center — Live',
  '/contact-center/email': 'Contact Center — Email',
  '/contact-center/city': 'Contact Center — City',
  '/contact-center/outbound': 'Contact Center — Outbound',
  '/contact-center/repeat-callers': 'Contact Center — Repeat Callers',
  '/contact-center/settings': 'Contact Center Settings',
  '/tv': 'TV Wall',
};

const labelOf = (route) => ROUTE_LABELS[route] || route;

// The actions the portal records. The list is an allow-list in
// services/usageAnalytics.js, and a filter's VALUE is never stored -- these say
// that somebody filtered, never what they typed into it.
const ACTION_LABELS = {
  export: 'Exported data',
  filter_changed: 'Changed a filter',
  range_changed: 'Changed the dates',
  dept_changed: 'Changed department',
  pod_changed: 'Changed pod',
  channel_changed: 'Changed channel',
  drilldown_opened: 'Opened a drill-down',
  tab_changed: 'Switched tab',
  search_used: 'Searched',
  upload_started: 'Started an upload',
  settings_saved: 'Saved settings',
  phone_revealed: 'Revealed a phone number',
};

// How a visit ended. Null means it had not ended when this was read -- either
// somebody is using the portal now, or housekeeping has not yet closed it.
const END_REASONS = {
  sign_out: 'Signed out',
  timeout: 'Went idle',
  expired: 'Session expired',
};

// ── Dates ───────────────────────────────────────────────────────────────────
// Pacific, because the server groups days in Pacific. Deriving the default
// range from the browser's own midnight would ask for a range one day wide of
// the one the figures are bucketed into, for anyone east of California.
const pacificToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());

function addDays(iso, n) {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

const PRESETS = [
  { days: 7,  label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ── Formatting ──────────────────────────────────────────────────────────────
const fmtInt = (n) => Number(n || 0).toLocaleString('en-US');

function fmtDuration(ms) {
  const secs = Math.round(Number(ms || 0) / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${secs % 60}s`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function fmtDay(iso) {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}

function fmtWhen(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: TZ,
  });
}

// The change against the range immediately before this one, of the same length.
// Nothing is shown when the previous range was empty: "up from zero" is a
// percentage of nothing, and reads as a finding when it is really a start.
function changeOf(now, before) {
  if (!before) return null;
  const pct = ((Number(now || 0) - before) / before) * 100;
  if (Math.abs(pct) < 0.5) return { pct: 0, dir: 'flat' };
  return { pct, dir: pct > 0 ? 'up' : 'down' };
}

// ── Small presentational pieces ─────────────────────────────────────────────

function Kpi({ icon: Icon, label, value, sub, change }) {
  const Arrow = change?.dir === 'up' ? TrendingUp : TrendingDown;
  return (
    <div style={s.kpi}>
      <div style={s.kpiTop}>
        <Icon size={13} color={c.textFaint} />
        <span style={s.kpiLabel}>{label}</span>
      </div>
      <div style={s.kpiValue}>{value}</div>
      <div style={s.kpiFoot}>
        {change && change.dir !== 'flat' ? (
          <span style={{ ...s.kpiChange, color: change.dir === 'up' ? c.successText : c.textMuted }}>
            <Arrow size={11} /> {Math.abs(change.pct).toFixed(0)}%
          </span>
        ) : change ? <span style={s.kpiFlat}>no change</span> : null}
        <span style={s.kpiSub}>{sub}</span>
      </div>
    </div>
  );
}

// A row's shape over the range, drawn small enough to sit in a table cell.
// Hand-rolled rather than a charting component: one <svg> per row against 500
// rows is cheap, and a Recharts container per row is not.
function Sparkline({ values }) {
  const width = 84;
  const height = 22;
  if (!values || values.length < 2) return <span style={s.dim}>—</span>;
  const max = Math.max(1, ...values);
  const step = width / (values.length - 1);
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(height - 1 - (v / max) * (height - 3)).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={width} height={height} role="img" aria-label="Daily page views" style={s.spark}>
      <polyline points={points} fill="none" stroke={chart.categorical[0]} strokeWidth="1.5"
        strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// Day of week against hour of day. A grid rather than a chart: the question is
// "when is the portal busy", which is read off a shape, and 168 cells is small
// enough that intensity carries it without axes.
function Heatmap({ hours }) {
  const byCell = new Map(hours.map((h) => [`${h.dow}:${h.hour}`, h.pageViews]));
  const max = Math.max(1, ...hours.map((h) => h.pageViews));
  const shade = (v) => {
    if (!v) return c.surfaceSunk;
    const band = Math.min(chart.sequential.length - 1, Math.floor((v / max) * chart.sequential.length));
    return chart.sequential[band];
  };
  return (
    <div style={s.heat} aria-label="Busiest times">
      <div style={s.heatHours}>
        <span style={s.heatCorner} />
        {Array.from({ length: 24 }, (_, h) => (
          <span key={h} style={s.heatHour}>{h % 6 === 0 ? `${h}` : ''}</span>
        ))}
      </div>
      {DOW.map((name, i) => (
        <div key={name} style={s.heatRow}>
          <span style={s.heatDow}>{name}</span>
          {Array.from({ length: 24 }, (_, h) => {
            const v = byCell.get(`${i + 1}:${h}`) || 0;
            return (
              <span key={h} style={{ ...s.heatCell, background: shade(v) }}
                title={`${name} ${h}:00 — ${fmtInt(v)} page views`} />
            );
          })}
        </div>
      ))}
    </div>
  );
}

// A labelled proportion bar, used for devices and the top-pages list.
function BarRow({ label, value, total, tone }) {
  const pct = total ? (value / total) * 100 : 0;
  return (
    <div style={s.barRow}>
      <span style={s.barLabel} title={label}>{label}</span>
      <span style={s.barTrack}>
        <span style={{ ...s.barFill, width: `${Math.max(pct, 1)}%`, background: tone }} />
      </span>
      <span style={s.barValue}>{fmtInt(value)}</span>
    </div>
  );
}

function DailyChart({ daily, label = 'Page views', tone = chart.categorical[0], showVisitors = true }) {
  const data = daily.map((d) => ({ ...d, label: fmtDay(d.day) }));
  return (
    <div style={s.chartBox}>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`usageViews-${label}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={tone} stopOpacity={0.28} />
              <stop offset="100%" stopColor={tone} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={chart.grid} vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: chart.axisText }}
            tickLine={false} axisLine={{ stroke: chart.grid }} minTickGap={24} />
          <YAxis tick={{ fontSize: 10, fill: chart.axisText }} tickLine={false} axisLine={false} width={40} />
          <Tooltip
            contentStyle={{ ...t.caption, border: `1px solid ${chart.tooltipBorder}`, borderRadius: r.md }}
            formatter={(v, k) => [fmtInt(v), k === 'pageViews' ? label : 'People']}
          />
          <Area type="monotone" dataKey="pageViews" stroke={tone} strokeWidth={1.8}
            fill={`url(#usageViews-${label})`} isAnimationActive={false} />
          {showVisitors && (
            <Area type="monotone" dataKey="visitors" stroke={chart.categorical[2]} strokeWidth={1.5}
              fill="none" isAnimationActive={false} />
          )}
        </AreaChart>
      </ResponsiveContainer>
      <div style={s.legend}>
        <span style={s.legendItem}><span style={{ ...s.swatch, background: tone }} /> {label}</span>
        {showVisitors && (
          <span style={s.legendItem}><span style={{ ...s.swatch, background: chart.categorical[2] }} /> People</span>
        )}
      </div>
    </div>
  );
}

// ── Tabs ────────────────────────────────────────────────────────────────────

function Overview({ params }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['usage-overview', params],
    queryFn:  () => api.get('/admin/usage/overview', { params }),
  });

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div style={s.card}><div style={s.err}>{error.message}</div></div>;
  if (!data) return null;

  const { totals, previous, daily, hours, devices, topPages } = data;
  if (!totals.pageViews) {
    return (
      <div style={s.card}>
        <div style={s.empty}>Nobody opened a page in this range.</div>
      </div>
    );
  }

  const deviceTotals = new Map();
  for (const d of devices) deviceTotals.set(d.device, (deviceTotals.get(d.device) || 0) + d.visits);
  const deviceRows = [...deviceTotals.entries()].sort((a, b) => b[1] - a[1]);
  const deviceSum = deviceRows.reduce((a, [, v]) => a + v, 0);
  const topPageSum = topPages.reduce((a, p) => a + p.pageViews, 0);

  return (
    <>
      <div style={s.kpiRow}>
        <Kpi icon={Eye}   label="Page views" value={fmtInt(totals.pageViews)}
          sub={`${fmtInt(previous.pageViews)} before`} change={changeOf(totals.pageViews, previous.pageViews)} />
        <Kpi icon={Users} label="People"     value={fmtInt(totals.visitors)}
          sub={`${fmtInt(previous.visitors)} before`} change={changeOf(totals.visitors, previous.visitors)} />
        <Kpi icon={Eye}   label="Visits"     value={fmtInt(totals.visits)}
          sub={`${fmtInt(previous.visits)} before`} change={changeOf(totals.visits, previous.visits)} />
        <Kpi icon={Timer} label="Engaged per visit" value={fmtDuration(totals.engagedPerVisitMs)}
          sub={`${fmtDuration(totals.engagedMs)} in total`}
          change={changeOf(totals.engagedPerVisitMs, previous.engagedPerVisitMs)} />
      </div>

      <div style={s.card}>
        <div style={s.cardHead}>Page views by day</div>
        <DailyChart daily={daily} />
      </div>

      <div style={s.split}>
        <div style={s.card}>
          <div style={s.cardHead}>Busiest times</div>
          <div style={s.cardBody}><Heatmap hours={hours} /></div>
        </div>
        <div style={s.card}>
          <div style={s.cardHead}>Devices</div>
          <div style={s.cardBody}>
            {deviceRows.map(([device, visits], i) => (
              <BarRow key={device} label={device} value={visits} total={deviceSum}
                tone={chart.coVisible[i % chart.coVisible.length]} />
            ))}
            <div style={s.subNote}>Visits, by the device the browser reported.</div>
          </div>
        </div>
      </div>

      <div style={s.card}>
        <div style={s.cardHead}>Most-opened pages</div>
        <div style={s.cardBody}>
          {topPages.map((p) => (
            <BarRow key={p.route} label={labelOf(p.route)} value={p.pageViews}
              total={topPageSum} tone={chart.categorical[0]} />
          ))}
        </div>
      </div>
    </>
  );
}

function PageDetail({ route, params, onBack }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['usage-page-detail', route, params],
    queryFn:  () => api.get('/admin/usage/pages/detail', { params: { ...params, route } }),
  });

  return (
    <div style={s.card}>
      <div style={s.detailHead}>
        <button style={s.backBtn} onClick={onBack}><ArrowLeft size={13} /> All pages</button>
        <div>
          <div style={s.detailTitle}>{labelOf(route)}</div>
          <div style={s.detailRoute}>{route}</div>
        </div>
      </div>

      {isLoading ? <LoadingSpinner /> : error ? <div style={s.err}>{error.message}</div> : data ? (
        <div style={s.cardBody}>
          <DailyChart daily={data.daily} />

          <div style={s.split}>
            <div>
              <div style={s.subHead}>Came from</div>
              {data.cameFrom.length ? data.cameFrom.map((f) => (
                <BarRow key={f.route || 'direct'}
                  label={f.route ? labelOf(f.route) : 'Opened directly'}
                  value={f.n} total={data.cameFrom[0].n} tone={chart.categorical[2]} />
              )) : <div style={s.subNote}>Nothing recorded.</div>}
            </div>
            <div>
              <div style={s.subHead}>Went to next</div>
              {data.wentTo.length ? data.wentTo.map((f) => (
                <BarRow key={f.route} label={labelOf(f.route)} value={f.n}
                  total={data.wentTo[0].n} tone={chart.categorical[1]} />
              )) : <div style={s.subNote}>Nothing recorded.</div>}
            </div>
          </div>

          <div style={s.subHead}>Who opened it</div>
          <div style={s.scroll}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Person</th>
                  <th style={s.thNum}>Views</th>
                  <th style={s.thNum}>Visits</th>
                  <th style={s.thNum}>Engaged</th>
                  <th style={s.th}>Last opened</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => (
                  <tr key={u.userId} style={s.tr}>
                    <td style={s.td}>
                      {/* A deleted user leaves page views behind; say so rather
                          than render a blank name. */}
                      <div style={s.userName}>{u.name || '(deleted user)'}</div>
                      <div style={s.userEmail}>{u.email || u.userId || '—'}</div>
                    </td>
                    <td style={s.tdNum}>{fmtInt(u.pageViews)}</td>
                    <td style={s.tdNum}>{fmtInt(u.visits)}</td>
                    <td style={s.tdNum}>{fmtDuration(u.engagedMs)}</td>
                    <td style={s.tdWhen}>{fmtWhen(u.lastViewed) || <span style={s.dim}>—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Pages({ params, route, onSelect }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['usage-pages', params],
    queryFn:  () => api.get('/admin/usage/pages', { params }),
  });

  if (route) return <PageDetail route={route} params={params} onBack={() => onSelect(null)} />;
  if (isLoading) return <LoadingSpinner />;
  if (error) return <div style={s.card}><div style={s.err}>{error.message}</div></div>;

  const items = data?.items || [];
  if (!items.length) {
    return <div style={s.card}><div style={s.empty}>Nobody opened a page in this range.</div></div>;
  }

  return (
    <div style={s.card}>
      <div style={s.scroll}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Page</th>
              <th style={s.thNum}>Views</th>
              <th style={s.thNum}>People</th>
              <th style={s.thNum}>Visits</th>
              <th style={s.thNum}>Median engaged</th>
              <th style={s.thNum}>Total engaged</th>
              <th style={s.th}>Trend</th>
              <th style={s.th}>Last opened</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.route} style={s.trClickable} onClick={() => onSelect(p.route)}>
                <td style={s.td}>
                  <div style={s.userName}>{labelOf(p.route)}</div>
                  <div style={s.userEmail}>{p.route}</div>
                </td>
                <td style={s.tdNum}>{fmtInt(p.pageViews)}</td>
                <td style={s.tdNum}>{fmtInt(p.visitors)}</td>
                <td style={s.tdNum}>{fmtInt(p.visits)}</td>
                <td style={s.tdNum}>{fmtDuration(p.medianEngagedMs)}</td>
                <td style={s.tdNum}>{fmtDuration(p.engagedMs)}</td>
                <td style={s.td}><Sparkline values={p.trend} /></td>
                <td style={s.tdWhen}>{fmtWhen(p.lastViewed) || <span style={s.dim}>—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={s.foot}>
        <span>{fmtInt(items.length)} pages opened in this range</span>
        <span>Click a page for who opened it and what they did next.</span>
      </div>
    </div>
  );
}

function PersonDetail({ userId, params, onBack }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['usage-person', userId, params],
    queryFn:  () => api.get('/admin/usage/people/detail', { params: { ...params, user_id: userId } }),
  });

  const p = data?.person;
  return (
    <div style={s.card}>
      <div style={s.detailHead}>
        <button style={s.backBtn} onClick={onBack}><ArrowLeft size={13} /> Everyone</button>
        <div>
          <div style={s.detailTitle}>{p?.name || 'Person'}</div>
          <div style={s.detailRoute}>
            {p?.email}{p?.role ? ` · ${p.role.replace('_', ' ')}` : ''}
            {p && !p.isActive ? ' · deactivated' : ''}
          </div>
        </div>
      </div>

      {isLoading ? <LoadingSpinner /> : error ? <div style={s.err}>{error.message}</div> : data ? (
        <div style={s.cardBody}>
          <DailyChart daily={data.daily} />

          <div style={s.split}>
            <div>
              <div style={s.subHead}>Pages they opened</div>
              {data.routes.length ? data.routes.map((rt) => (
                <BarRow key={rt.route} label={labelOf(rt.route)} value={rt.pageViews}
                  total={data.routes[0].pageViews} tone={chart.categorical[0]} />
              )) : <div style={s.subNote}>Nothing in this range.</div>}
            </div>
            <div>
              <div style={s.subHead}>What they did</div>
              {data.actions.length ? data.actions.map((a) => (
                <BarRow key={a.name} label={ACTION_LABELS[a.name] || a.name} value={a.n}
                  total={data.actions[0].n} tone={chart.categorical[1]} />
              )) : <div style={s.subNote}>No recorded actions in this range.</div>}
            </div>
          </div>

          <div style={s.subHead}>Visits</div>
          <div style={s.scroll}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Started</th>
                  <th style={s.th}>Started on</th>
                  <th style={s.thNum}>Pages</th>
                  <th style={s.th}>Ended</th>
                  <th style={s.th}>Device</th>
                </tr>
              </thead>
              <tbody>
                {data.visits.map((v) => (
                  <tr key={v.sessionId} style={s.tr}>
                    <td style={s.tdWhen}>{fmtWhen(v.startedAt)}</td>
                    <td style={s.td}>{v.firstRoute ? labelOf(v.firstRoute) : <span style={s.dim}>—</span>}</td>
                    <td style={s.tdNum}>{fmtInt(v.pageViews)}</td>
                    <td style={s.td}>
                      {/* No end reason means the visit had not been closed when
                          this was read -- either it is still going, or the
                          half-hourly housekeeping has not caught it yet. */}
                      {v.endReason
                        ? `${END_REASONS[v.endReason] || v.endReason}${v.endedAt ? ` · ${fmtWhen(v.endedAt)}` : ''}`
                        : <span style={s.dim}>still open</span>}
                    </td>
                    <td style={s.td}>
                      {[v.browser, v.os, v.device].filter(Boolean).join(' · ') || <span style={s.dim}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={s.subHead}>Pages they did not open</div>
          {data.unopened.length ? (
            <>
              <div style={s.chips}>
                {data.unopened.map((u) => (
                  <span key={u.route} style={s.chip} title={`${u.people} other people opened it`}>
                    {labelOf(u.route)}
                  </span>
                ))}
              </div>
              {/* Said precisely, because the obvious reading is wrong: this is
                  what colleagues opened and this person did not, which is NOT
                  the same as what they are allowed to open. Access is a role, a
                  standalone permission and on several boards a cost-centre
                  assignment, and nothing in these tables knows any of it. */}
              <div style={s.subNote}>
                Pages other people opened in this range and this person did not. That is
                not a permission check — someone may simply have no reason to open a page
                they can reach, and several boards are further limited by cost center.
              </div>
            </>
          ) : <div style={s.subNote}>They opened every page anyone else did.</div>}
        </div>
      ) : null}
    </div>
  );
}

function People({ params, userId, onSelect }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['usage-people', params],
    queryFn:  () => api.get('/admin/usage/people', { params }),
  });

  if (userId) return <PersonDetail userId={userId} params={params} onBack={() => onSelect(null)} />;
  if (isLoading) return <LoadingSpinner />;
  if (error) return <div style={s.card}><div style={s.err}>{error.message}</div></div>;

  const items = data?.items || [];
  if (!items.length) return <div style={s.card}><div style={s.empty}>Nobody opened a page in this range.</div></div>;

  return (
    <div style={s.card}>
      <div style={s.scroll}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Person</th>
              <th style={s.thNum}>Views</th>
              <th style={s.thNum}>Visits</th>
              <th style={s.thNum}>Pages</th>
              <th style={s.thNum}>Engaged</th>
              <th style={s.thNum}>Per visit</th>
              <th style={s.th}>Most opened</th>
              <th style={s.th}>Last seen</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.userId} style={s.trClickable} onClick={() => onSelect(p.userId)}>
                <td style={s.td}>
                  <div style={s.userName}>
                    {p.name || '(deleted user)'}
                    {p.isActive === false ? <span style={s.tag}>deactivated</span> : null}
                  </div>
                  <div style={s.userEmail}>{p.email || p.userId}</div>
                </td>
                <td style={s.tdNum}>{fmtInt(p.pageViews)}</td>
                <td style={s.tdNum}>{fmtInt(p.visits)}</td>
                <td style={s.tdNum}>{fmtInt(p.pages)}</td>
                <td style={s.tdNum}>{fmtDuration(p.engagedMs)}</td>
                <td style={s.tdNum}>{fmtDuration(p.engagedPerVisitMs)}</td>
                <td style={s.td}>{p.topRoute ? labelOf(p.topRoute) : <span style={s.dim}>—</span>}</td>
                <td style={s.tdWhen}>{fmtWhen(p.lastSeen) || <span style={s.dim}>—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={s.foot}>
        <span>{fmtInt(items.length)} people used the portal in this range</span>
        <span>Click a person for their visits and what they did.</span>
      </div>
    </div>
  );
}

function Errors({ params }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['usage-errors', params],
    queryFn:  () => api.get('/admin/usage/errors', { params }),
  });

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div style={s.card}><div style={s.err}>{error.message}</div></div>;
  if (!data) return null;

  if (!data.totals.occurrences) {
    return (
      <div style={s.card}>
        <div style={s.empty}>
          No browser errors in this range.
          <div style={s.emptySub}>
            Errors are reported by the browser as they happen. Silence here means nobody&apos;s
            page threw — not that nothing is being watched.
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={s.kpiRow}>
        <Kpi icon={AlertTriangle} label="Errors" value={fmtInt(data.totals.occurrences)} sub="in this range" />
        <Kpi icon={Users} label="People affected" value={fmtInt(data.totals.people)} sub="hit at least one" />
      </div>

      <div style={s.card}>
        <div style={s.cardHead}>Errors by day</div>
        <DailyChart daily={data.daily} label="Errors" tone={c.danger} showVisitors={false} />
      </div>

      <div style={s.card}>
        <div style={s.scroll}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>What broke</th>
                <th style={s.th}>Page</th>
                <th style={s.thNum}>People</th>
                <th style={s.thNum}>Times</th>
                <th style={s.th}>First seen</th>
                <th style={s.th}>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((e, i) => (
                <tr key={`${e.message}:${e.route}:${i}`} style={s.tr}>
                  <td style={s.tdMessage} title={e.message}>{e.message}</td>
                  <td style={s.td}>
                    {e.route ? labelOf(e.route) : <span style={s.dim}>—</span>}
                    {e.build ? <div style={s.userEmail}>build {e.build}</div> : null}
                  </td>
                  {/* People before times: one person clicking a broken button
                      eight times is one problem, not eight. */}
                  <td style={s.tdNum}>{fmtInt(e.people)}</td>
                  <td style={s.tdNum}>{fmtInt(e.occurrences)}</td>
                  <td style={s.tdWhen}>{fmtWhen(e.firstSeen)}</td>
                  <td style={s.tdWhen}>{fmtWhen(e.lastSeen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={s.foot}>
          <span>Ranked by how many people were affected.</span>
          <span>Messages are trimmed to 300 characters as they are recorded.</span>
        </div>
      </div>
    </>
  );
}

function Adoption({ params, onSelectPerson }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['usage-adoption', params],
    queryFn:  () => api.get('/admin/usage/adoption', { params }),
  });

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div style={s.card}><div style={s.err}>{error.message}</div></div>;
  if (!data) return null;

  const roster = (rows, whenLabel, whenOf) => (
    <div style={s.scroll}>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Person</th>
            <th style={s.th}>Role</th>
            <th style={s.th}>{whenLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.userId} style={s.trClickable} onClick={() => onSelectPerson(p.userId)}>
              <td style={s.td}>
                <div style={s.userName}>{p.name}</div>
                <div style={s.userEmail}>{p.email}</div>
              </td>
              <td style={s.td}>{(p.role || '').replace('_', ' ')}</td>
              <td style={s.tdWhen}>{fmtWhen(whenOf(p)) || <span style={s.dim}>never</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div style={s.kpiRow}>
        <Kpi icon={Users} label="Active accounts" value={fmtInt(data.activeUsers)} sub="not deactivated" />
        <Kpi icon={Eye} label="Used it" value={fmtInt(data.reachedUsers)} sub="in this range" />
        <Kpi icon={UserMinus} label="Gone quiet" value={fmtInt(data.quiet.length)} sub="used it before, not now" />
        <Kpi icon={UserMinus} label="Never used it" value={fmtInt(data.neverUsed.length)} sub="no page view ever" />
      </div>

      <div style={s.card}>
        <div style={s.cardHead}>Never opened a page</div>
        <div style={s.cardBody}>
          {data.neverUsed.length ? roster(data.neverUsed, 'Last signed in', (p) => p.lastLoginAt)
            : <div style={s.subNote}>Every active account has used the portal.</div>}
          {/* The distinction matters and is easy to misread: sign-ins come from
              audit_log and go back to migration 035, while these tables start
              when telemetry was switched on. Somebody who stopped using the
              portal before that shows here with an old sign-in date. */}
          <div style={s.subNote}>
            &ldquo;Never&rdquo; means no page view recorded. Page views only start from the day
            telemetry was switched on, so an older sign-in date here means they used the
            portal before it was being measured — not that they signed in and did nothing.
          </div>
        </div>
      </div>

      <div style={s.card}>
        <div style={s.cardHead}>Gone quiet</div>
        <div style={s.cardBody}>
          {data.quiet.length ? roster(data.quiet, 'Last seen', (p) => p.lastSeen)
            : <div style={s.subNote}>Everyone who has used the portal used it in this range.</div>}
        </div>
      </div>

      <div style={s.card}>
        <div style={s.cardHead}>How far each page reached</div>
        <div style={s.cardBody}>
          {data.reach.map((rt) => (
            <BarRow key={rt.route} label={`${labelOf(rt.route)} · ${rt.pctOfActive}%`}
              value={rt.people} total={data.activeUsers} tone={chart.categorical[0]} />
          ))}
          <div style={s.subNote}>
            People who opened each page, against {fmtInt(data.activeUsers)} active accounts. A low
            figure is not necessarily a problem — most pages are meant for some people and not
            others, and nothing here knows who is allowed to open what.
          </div>
        </div>
      </div>
    </>
  );
}

// ── The page ────────────────────────────────────────────────────────────────

export default function Usage() {
  const today = pacificToday();
  const [range, setRange] = useState({ from: addDays(today, -29), to: today });
  const [tab, setTab] = useState('overview');
  const [route, setRoute] = useState(null);
  const [person, setPerson] = useState(null);
  const [exporting, setExporting] = useState(false);

  // One params object drives the Overview, the Pages table, the page detail and
  // the workbook, so a spreadsheet can never disagree with the screen — the
  // same reason routes/accessLogAdmin.js shares its filter builder.
  const params = { from: range.from, to: range.to };

  function setDates(next) {
    setRange((cur) => ({ ...cur, ...next }));
    // A detail read under one range, left on screen beside a table from
    // another, is two different questions answered as one.
    setRoute(null);
    setPerson(null);
  }

  // Jumping to a person from Adoption has to land on the tab that renders
  // them, or the click sets state nothing is showing and reads as broken.
  function showPerson(userId) {
    setPerson(userId);
    setTab('people');
  }

  function preset(days) {
    const to = pacificToday();
    setDates({ from: addDays(to, -(days - 1)), to });
  }

  // Only the two tabs that ARE a table can be exported. An export button on
  // the Overview would have to invent a shape for a page of charts.
  const exportable = tab === 'pages' || tab === 'people';

  async function runExport() {
    setExporting(true);
    try {
      const qs   = new URLSearchParams(params).toString();
      const blob = await api.getBlob(`/admin/usage/${tab}/export?${qs}`);
      const url  = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `usage-${tab}-${range.from}-to-${range.to}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message || 'Export failed.');
    } finally {
      setExporting(false);
    }
  }

  const activePreset = PRESETS.find((p) => range.to === today && range.from === addDays(today, -(p.days - 1)));

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Administration"
        title="Usage"
        subtitle="Which pages of the portal get opened, by whom, and for how long. This names people."
        actions={exportable ? (
          <button style={s.exportBtn} onClick={runExport} disabled={exporting}>
            <Download size={13} /> {exporting ? 'Preparing…' : `Export ${tab}`}
          </button>
        ) : null}
      />

      <div style={s.filterRow}>
        <div style={s.group}>
          <label style={s.label} htmlFor="usage-from">From</label>
          <input id="usage-from" type="date" style={s.input}
            value={range.from} onChange={(e) => setDates({ from: e.target.value })} />
        </div>
        <div style={s.group}>
          <label style={s.label} htmlFor="usage-to">To</label>
          <input id="usage-to" type="date" style={s.input}
            value={range.to} onChange={(e) => setDates({ to: e.target.value })} />
        </div>
        <div style={s.presets}>
          {PRESETS.map((p) => (
            <button key={p.days}
              style={{ ...s.presetBtn, ...(activePreset?.days === p.days ? s.presetActive : {}) }}
              onClick={() => preset(p.days)}>{p.label}</button>
          ))}
        </div>
      </div>

      <div style={s.tabRow} role="tablist" aria-label="Usage views">
        {[['overview', 'Overview'], ['pages', 'Pages'], ['people', 'People'],
          ['errors', 'Errors'], ['adoption', 'Adoption']].map(([key, name]) => (
          <button key={key} role="tab" aria-selected={tab === key}
            style={{ ...s.tab, ...(tab === key ? s.tabActive : {}) }}
            onClick={() => setTab(key)}>{name}</button>
        ))}
      </div>

      {tab === 'overview' && <Overview params={params} />}
      {tab === 'pages'    && <Pages params={params} route={route} onSelect={setRoute} />}
      {tab === 'people'   && <People params={params} userId={person} onSelect={setPerson} />}
      {tab === 'errors'   && <Errors params={params} />}
      {tab === 'adoption' && <Adoption params={params} onSelectPerson={showPerson} />}

      <div style={s.note}>
        Days are Pacific, and both dates are included. A visit is one stretch of
        use: thirty minutes without activity ends it, and the next page opened
        starts a new one. Engaged time counts only while the tab was visible and
        focused, so a page left open in the background adds nothing.
        <br /><br />
        <strong>This page names colleagues and what they opened.</strong> It exists because
        the portal holds named-employee wage and customer data, and it is restricted to
        super administrators — the same restriction as the Access Log, enforced on the
        page, the route and every endpoint behind it. Reading it is itself recorded in
        the Access Log. It records which pages were opened and which named actions were
        taken; it does not record what anyone typed into a filter or search box.
        <br /><br />
        These figures are analytics, not the audit record: they are kept for thirteen
        months and then deleted, and they only go back to the day telemetry was switched
        on. The Access Log is the durable record and reaches much further back, so any
        question that has to be answered months from now should be asked there.
      </div>
    </div>
  );
}

const s = {
  page: { padding: `${sp[6]} ${sp[7]}`, maxWidth: '1400px', margin: '0 auto' },

  filterRow: { display: 'flex', gap: sp[3], flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: sp[4] },
  group: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { ...t.micro, fontWeight: 600, color: c.textMuted },
  input: {
    padding: '7px 10px', border: `1px solid ${c.borderStrong}`, borderRadius: r.md,
    ...t.bodySm, color: c.textStrong, fontFamily: 'inherit', background: c.surface,
  },
  presets: { display: 'flex', gap: sp[2] },
  presetBtn: { ...btn.secondaryXs, background: c.surface },
  presetActive: { background: c.navy, color: c.onNavy, borderColor: c.navy },
  exportBtn: { ...btn.secondarySm, gap: sp[1] },

  tabRow: { display: 'flex', borderBottom: `1px solid ${c.border}`, marginBottom: sp[5], gap: sp[6] },
  tab: {
    background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-1px',
    padding: `${sp[2]} 2px`, ...t.bodySm, fontWeight: 600, color: c.textFaint,
    fontFamily: 'inherit', cursor: 'pointer',
  },
  // borderBottom in full, not borderBottomColor: React warns when a rerender
  // drops a longhand while the shorthand is still set, and the underline then
  // depends on which of the two won.
  tabActive: { color: c.navy, borderBottom: `2px solid ${c.navy}` },

  kpiRow: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: sp[4], marginBottom: sp[5],
  },
  kpi: { ...card.base, padding: sp[4] },
  kpiTop: { display: 'flex', alignItems: 'center', gap: sp[2], marginBottom: sp[2] },
  kpiLabel: { ...t.micro, fontWeight: 600, color: c.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' },
  kpiValue: { ...t.h1, color: c.navy, ...t.tabular },
  kpiFoot: { display: 'flex', alignItems: 'center', gap: sp[2], marginTop: sp[2] },
  kpiChange: { display: 'inline-flex', alignItems: 'center', gap: '3px', ...t.micro, fontWeight: 600 },
  kpiFlat: { ...t.micro, color: c.textFaint },
  kpiSub: { ...t.micro, color: c.textFaint },

  card: { ...card.base, overflow: 'hidden', marginBottom: sp[5] },
  cardHead: { ...t.h3, color: c.textStrong, padding: `${sp[4]} ${sp[4]} 0` },
  cardBody: { padding: sp[4] },
  split: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: sp[4] },

  chartBox: { padding: `${sp[3]} ${sp[2]} 0` },
  legend: { display: 'flex', gap: sp[4], padding: `0 ${sp[4]} ${sp[3]}`, ...t.micro, color: c.textMuted },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: sp[2] },
  swatch: { width: '9px', height: '9px', borderRadius: '2px', display: 'inline-block' },

  heat: { display: 'flex', flexDirection: 'column', gap: '2px' },
  heatHours: { display: 'flex', gap: '2px', alignItems: 'center' },
  heatCorner: { width: '28px', flexShrink: 0 },
  heatHour: { flex: 1, ...t.micro, fontSize: '9px', color: chart.axisText, textAlign: 'left', minWidth: 0 },
  heatRow: { display: 'flex', gap: '2px', alignItems: 'center' },
  heatDow: { width: '28px', flexShrink: 0, ...t.micro, fontSize: '9px', color: c.textMuted },
  heatCell: { flex: 1, height: '14px', borderRadius: '2px', minWidth: 0 },

  barRow: { display: 'flex', alignItems: 'center', gap: sp[3], marginBottom: sp[2] },
  barLabel: {
    ...t.bodySm, color: c.textStrong, width: '190px', flexShrink: 0,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  barTrack: { flex: 1, height: '8px', background: c.surfaceSunk, borderRadius: r.pill, overflow: 'hidden' },
  barFill: { display: 'block', height: '100%', borderRadius: r.pill },
  barValue: { ...t.bodySm, ...t.tabular, color: c.textMuted, width: '64px', textAlign: 'right', flexShrink: 0 },

  detailHead: {
    display: 'flex', alignItems: 'center', gap: sp[4],
    padding: sp[4], borderBottom: `1px solid ${c.border}`,
  },
  backBtn: { ...btn.secondaryXs, gap: sp[1] },
  detailTitle: { ...t.h3, color: c.textStrong },
  detailRoute: { ...t.micro, color: c.textFaint },
  subHead: { ...t.micro, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: c.textMuted, margin: `${sp[4]} 0 ${sp[3]}` },
  subNote: { ...t.micro, color: c.textFaint, marginTop: sp[2] },

  scroll: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { ...tbl.th, whiteSpace: 'nowrap' },
  thNum: { ...tbl.th, whiteSpace: 'nowrap', textAlign: 'right' },
  tr: { ...tbl.tr },
  trClickable: { ...tbl.tr, cursor: 'pointer' },
  td: { ...tbl.td },
  tdNum: { ...tbl.td, whiteSpace: 'nowrap', textAlign: 'right', ...t.tabular },
  tdWhen: { ...tbl.td, whiteSpace: 'nowrap', ...t.tabular, color: c.textMuted },
  userName: { ...t.bodySm, color: c.textStrong, fontWeight: 600 },
  userEmail: { ...t.micro, color: c.textFaint },
  dim: { color: c.textFaint },
  spark: { display: 'block' },

  foot: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: `${sp[3]} ${sp[4]}`, ...t.micro, color: c.textFaint,
    borderTop: `1px solid ${c.border}`, background: c.surfaceSunk,
  },

  note: { ...t.micro, color: c.textFaint, marginTop: sp[4], lineHeight: 1.6, maxWidth: '80ch' },
  empty: { padding: sp[8], textAlign: 'center', ...t.bodySm, color: c.textFaint },
  emptySub: { ...t.micro, color: c.textFaint, marginTop: sp[3], maxWidth: '52ch', marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 },

  tag: {
    ...t.micro, fontWeight: 600, marginLeft: sp[2], padding: '1px 6px',
    borderRadius: r.pill, background: c.warningTint, color: c.warningText,
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: sp[2] },
  chip: {
    ...t.micro, padding: '3px 9px', borderRadius: r.pill,
    background: c.surfaceSunk, color: c.textMuted, border: `1px solid ${c.border}`,
    whiteSpace: 'nowrap',
  },
  tdMessage: {
    ...tbl.td, maxWidth: '420px', overflow: 'hidden', textOverflow: 'ellipsis',
    whiteSpace: 'nowrap', color: c.textStrong, fontFamily: t.familyMono, fontSize: '12px',
  },
  err: { padding: sp[5], ...t.bodySm, color: c.danger },
};
